import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ttStatsAPI } from '../services/api';
import { Trophy, TrendingUp, TrendingDown } from 'lucide-react';

const LeaguePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [leagueData, setLeagueData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadLeagueData(decodeURIComponent(id));
    }
  }, [id]);

  const loadLeagueData = async (url: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await ttStatsAPI.getLeagueStandings(url);
      setLeagueData(data);
    } catch (err) {
      console.error('Error loading league data:', err);
      setError('Failed to load league data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 transition-colors duration-300">Loading league standings...</p>
        </div>
      </div>
    );
  }

  if (error || !leagueData) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg p-6 text-center transition-colors duration-300">
        <p className="text-red-600 dark:text-red-400 transition-colors duration-300">{error || 'League data not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* League Header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
        <div className="flex items-center gap-4">
          <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-xl transition-colors duration-300">
            <Trophy className="h-8 w-8 text-yellow-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">League Standings</h1>
            <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">{leagueData.standings?.length || 0} teams</p>
          </div>
        </div>
      </div>

      {/* Standings Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300">
        {leagueData.standings && leagueData.standings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 transition-colors duration-300">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-300">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-300">
                    Team
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-300">
                    Games
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-300">
                    Points
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-300">
                    Ratio
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 transition-colors duration-300">
                {leagueData.standings.map((team: any, index: number) => {
                  const position = team.position || index + 1;
                  const isTop3 = position <= 3;
                  const isBottom2 = position >= leagueData.standings.length - 1;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {isTop3 && (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          )}
                          {isBottom2 && (
                            <TrendingDown className="h-4 w-4 text-red-500 dark:text-red-400" />
                          )}
                          <span className={`font-bold text-lg ${
                            position === 1 ? 'text-yellow-500' :
                            position === 2 ? 'text-gray-400' :
                            position === 3 ? 'text-orange-600' :
                            'text-gray-700 dark:text-gray-300'
                          } transition-colors duration-300`}>
                            {position}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">{team.name}</p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-gray-700 dark:text-gray-300 transition-colors duration-300">{team.games}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-semibold text-blue-600 transition-colors duration-300">{team.points}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-gray-700 dark:text-gray-300 transition-colors duration-300">{team.ratio}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 transition-colors duration-300">
            No standings data available
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 transition-colors duration-300">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Legend:</p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-gray-700 dark:text-gray-300 transition-colors duration-300">Promotion Zone</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-500 dark:text-red-400" />
            <span className="text-gray-700 dark:text-gray-300 transition-colors duration-300">Relegation Zone</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaguePage;